import React, { useState, useEffect } from "react";
import {
    Layout, Button, Form, Row, Col, Breadcrumb, message, Select, DatePicker, Input,Space
} from "antd";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import {
    PlusOutlined, HomeOutlined, SnippetsOutlined, ArrowLeftOutlined
} from "@ant-design/icons";
import api from "../../services/axiosInstance";
import { useNavigate } from "react-router-dom";
import "../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;
const { TextArea } = Input;

const AddMaintenance = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    
    const [maintainCats, setMaintainCats] = useState([]);
    const [lands, setLands] = useState([]);
    const [sections, setSections] = useState([]);
    const [fields, setFields] = useState([]);
    const [plants, setPlants] = useState([]);
    
    const [level, setLevel] = useState('land');

    const fetchData = async () => {
        try {
            const [catsRes, landsRes] = await Promise.all([
                api.get('/masterdropdown/listof_activemaintaincategories.php'),
                api.get('/masterdropdown/listof_activelands.php')
            ]);
            setMaintainCats(catsRes.data.dataset);
            setLands(landsRes.data.dataset);
        } catch {
            message.error("Failed to load reference data.");
        }
    };

    useEffect(() => { fetchData(); }, []);

    const fetchSectionsByLand = async (landId) => {
        try {
            const res = await api.get(`/masterdropdown/listof_activesections.php?land_id=${landId}`);
            setSections(res.data.dataset);
        } catch { message.error("Failed to load sections."); }
    };

    const fetchFieldsBySection = async (sectionId) => {
        try {
            const res = await api.get(`/masterdropdown/listof_activefields.php?section_id=${sectionId}`);
            setFields(res.data.dataset);
        } catch { message.error("Failed to load fields."); }
    };

    const fetchPlantsByField = async (fieldId) => {
        try {
            // Need a new master dropdown for plants by field
            const res = await api.get(`/farmSetUp/plant/list.php?field_id=${fieldId}&limit=100`);
            setPlants(res.data.dataset.map(p => ({ value: p.plant_id, label: p.plant_code })));
        } catch { message.error("Failed to load plants."); }
    };

    const onFinish = async (values) => {
        try {
            setLoading(true);
            let targetId = 0;
            switch (level) {
                case 'land':    targetId = values.landId; break;
                case 'section': targetId = values.sectionId; break;
                case 'field':   targetId = values.fieldId; break;
                case 'plant':   targetId = values.plantId; break;
            }

            const res = await api.post('/maintenance/add.php', {
                type: level,
                target_id: targetId,
                mc_id: values.mcId,
                target_date: values.targetDate.format('YYYY-MM-DD'),
                title: values.title,
                desc: values.desc
            });

            if (res.data.status === 'success') {
                message.success(res.data.message);
                navigate('/maintenance');
            }
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to create task.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { href: '/maintenance', title: 'Maintenance' },
                                    { title: 'New Task' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <SnippetsOutlined /> Create Maintenance Task
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Schedule a new maintenance activity for farm assets</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <Form form={form} layout="vertical" onFinish={onFinish}>
                        <Row gutter={16}>
                            <Col xs={24} md={8}>
                                <Form.Item label="Maintenance Level" name="level" initialValue="land">
                                    <Select allowClear onChange={(val) => { setLevel(val); form.setFieldsValue({ sectionId: undefined, fieldId: undefined, plantId: undefined }); }}>
                                        <Option value="land">Land Level</Option>
                                        <Option value="section">Section Level</Option>
                                        <Option value="field">Field Level</Option>
                                        <Option value="plant">Plant Level</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Category" name="mcId" rules={[{ required: true }]}>
                                    <Select allowClear placeholder="Select Category">
                                        {maintainCats.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Target Date" name="targetDate" rules={[{ required: true }]}>
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col xs={24} md={6}>
                                <Form.Item label="Land" name="landId" rules={[{ required: true }]}>
                                    <Select allowClear placeholder="Select Land" onChange={(val) => { setSections([]); setFields([]); setPlants([]); form.setFieldsValue({ sectionId: undefined, fieldId: undefined, plantId: undefined }); fetchSectionsByLand(val); }}>
                                        {lands.map(l => <Option key={l.value} value={l.value}>{l.label}</Option>)}
                                    </Select>
                                </Form.Item>
                            </Col>
                            
                            {(level === 'section' || level === 'field' || level === 'plant') && (
                                <Col xs={24} md={6}>
                                    <Form.Item label="Section" name="sectionId" rules={[{ required: true }]}>
                                        <Select allowClear placeholder="Select Section" disabled={!sections.length} onChange={(val) => { setFields([]); setPlants([]); form.setFieldsValue({ fieldId: undefined, plantId: undefined }); fetchFieldsBySection(val); }}>
                                            {sections.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            )}

                            {(level === 'field' || level === 'plant') && (
                                <Col xs={24} md={6}>
                                    <Form.Item label="Field" name="fieldId" rules={[{ required: true }]}>
                                        <Select allowClear placeholder="Select Field" disabled={!fields.length} onChange={(val) => { setPlants([]); form.setFieldsValue({ plantId: undefined }); fetchPlantsByField(val); }}>
                                            {fields.map(f => <Option key={f.value} value={f.value}>{f.label}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            )}

                            {level === 'plant' && (
                                <Col xs={24} md={6}>
                                    <Form.Item label="Plant" name="plantId" rules={[{ required: true }]}>
                                        <Select allowClear placeholder="Select Plant" disabled={!plants.length}>
                                            {plants.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>

                        <Form.Item label="Task Title" name="title" rules={[{ required: true }]}>
                            <Input placeholder="e.g. Weekly Irrigation" />
                        </Form.Item>

                        <Form.Item label="Description" name="desc">
                            <TextArea rows={4} placeholder="Details about the maintenance task..." />
                        </Form.Item>

                        <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>
                                    Create Task
                                </Button>
                                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/maintenance')}>
                                    Back to List
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </div>
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default AddMaintenance;
